import React, { useState } from "react";
import { Modal } from "../modal";
import { useBasketStore, useProductStore, useRapidDeliveryStore } from "../../store/hooks/useStore";
import { Box } from "../../vanilla";
import { observer } from "mobx-react-lite";
import { ViewAlternativesButton } from "../view-alternatives-button";
import { Text, BaseButton } from "../../vanilla";
import { InfoIcon } from "../icons";
import { getUnavailableProductReason, isMobile, UNAVAILABLE_REASON_RAPID } from "../../utils/utils";
import { useSelectAlternativesTracking } from "../../analytics/click-event-tracking";

export const UnavailableProductModal = observer(() => {
  const basketStore = useBasketStore()

  const isMobileFlag = isMobile()
  const productStore = useProductStore()
  const { productsById } = productStore
  const unavailableProduct = productsById[basketStore.openProductUnavailableModal?.productId || '']
  const { isRapidDeliveryBooked } = useRapidDeliveryStore()
  const isUnavailableOnlyForRapid =
    getUnavailableProductReason(unavailableProduct, isRapidDeliveryBooked) === UNAVAILABLE_REASON_RAPID
  const selectAlternativesTracking = useSelectAlternativesTracking()
  const [alternativesPath, setAlternativesPath] = useState<string | undefined>(undefined)

  const alternativesTracking = (modalDismissed: boolean, dismissalMethod: string | undefined) => { 
    const alternativesTrackingData = {
      unavailableProduct: unavailableProduct,
      storeId: (basketStore.currentDeliveryStoreId || 0),
      alternativesPath: alternativesPath,
      modalDismissed: modalDismissed,
      dismissalMethod: dismissalMethod,
      itemLocation: basketStore.locationOfCart,
    }
    selectAlternativesTracking(alternativesTrackingData)
  }

  const onClose = (modalDismissed: boolean, dismissalMethod?: string | undefined) => {
    alternativesTracking(modalDismissed, (modalDismissed ? dismissalMethod : undefined))
    
    if (basketStore.openProductUnavailableModal?.open) {
      basketStore.setOpenProductUnavailableModal({ productId: '', open: false })
    }
  }

  if (basketStore.openProductUnavailableModal.open === false) { return null }


  return (
    <Modal
      onClose={(dismissalMethod) => { onClose(true, dismissalMethod) }}
      isOpen={basketStore.openProductUnavailableModal?.open}
      size={isMobileFlag ? "unavailableProductModal" : "lg"}
      allowScroll={true}
      unavailableProductModal={true}
      showTitleBorder={false}
      data-test-selector="unavailable-product-modal"
      style={{
        border: '1px',
        borderRadius: 'medium',
        borderColor: 'gray300',
        flexWrap: 'nowrap',
        width: 'auto',
        boxSize: '14px',
        maxHeight: '300px',
      }}
      closeIcon={true}
      title={
        <Text
          padding="0px"
          variant={isMobileFlag ? "text7" : "heading1"}
          fontWeight={'bold'}
          style={{ width: 'auto' }}
          maxHeight={'48px'}
        >
          Sorry, this product isn't available right now
        </Text>}
    >
      <Box
        style={{
          alignSelf: 'left',
          padding: '8px',
          display: 'flex',
          flexDirection: 'row',
          flex: '1',
          flexShrink: '3',
        }}
        backgroundColor={'warningBackground'}
        maxWidth={'540px'}
        marginX={'28px'}
        border={'1px'}
        borderColor={'warningBorder'}
        borderRadius={'medium'}
      >
        <InfoIcon boxSize={'16px'} alignSelf={'flex-start'} flexShrink={'0'} />
        <Text borderRadius={'base'} variant="text2" lineHeight="shorter" marginLeft={"8px"} color={'warningText'}>
          {isUnavailableOnlyForRapid
            ? "This product isn't available for Rapid delivery and was not added to your basket. View alternative products or book a different delivery slot."
            : 'The product you selected is not currently available for delivery in your area and was not added to your basket.'}
        </Text>
      </Box>
      <Box
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'right',
          justifyContent: 'right',
          flex: '1',
          flexShrink: '1',
        }}
        marginX={'28px'}
        marginY={'16px'}
        flexDirection={isMobileFlag ? 'column' : 'row'}>
        <BaseButton
          order={isMobileFlag ? '2' : '1'}
          style={{ borderColor: 'accent1' }}
          size="sm" 
          variant="outlineLight"
          onClick={() => {
            onClose(true, 'close_button')
          }}
          data-test-selector="unavailable-modal-close-button"
        >
          Close
        </BaseButton>
        <ViewAlternativesButton
          order={isMobileFlag ? '1' : '2'}
          fontWeight={'bold'}
          size='sm'
          variant={'primary'}
          product={unavailableProduct}
          isProductUnavailableModal={true}
          onClick={() => {
            onClose(false)
          }}
          data-test-selector="unavailable-modal-alternatives-button"
          setAlternativesUrl={(path: string) => setAlternativesPath(path)}
        />
      </Box>
    </Modal>
  )
})